// LUMINA · utils/pushNotifications.js
// Phase 58: Web Push subscription helper

import { API } from '../constants/config';

// VAPID public key from env — set REACT_APP_VAPID_PUBLIC_KEY in .env
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export async function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function getPushPermission() {
  return Notification.permission;  // 'granted' | 'denied' | 'default'
}

export async function subscribePush(token) {
  if (!await isPushSupported()) throw new Error("Push not supported");
  if (!VAPID_PUBLIC_KEY) throw new Error("VAPID_PUBLIC_KEY not configured");

  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error("Push permission denied");

  // Subscribe to push manager
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });

  const subJSON = sub.toJSON();
  const keys = subJSON.keys || {};

  // Send to backend
  await fetch(`${API}/auth/push-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      p256dh:   keys.p256dh || '',
      auth:     keys.auth   || '',
    }),
  });

  return sub;
}

export async function unsubscribePush(token) {
  if (!await isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  const subJSON = sub.toJSON();
  const keys = subJSON.keys || {};

  await fetch(`${API}/auth/push-subscription`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      p256dh:   keys.p256dh || '',
      auth:     keys.auth   || '',
    }),
  });

  await sub.unsubscribe();
}

export async function getCurrentSubscription() {
  if (!await isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}
